/**
 * @description It aggregates all the error codes that can be thrown by the SDK.
 * It should be the only source of truth for the error codes.
 * You should separate each error type by a blank line.
 * Use `_` to separate the group of digits
 */
export enum SdkErrorCodes {
  DEFAULT_ERROR = 10_000,
  INVALID_PARAMS = 10_001,
  INVALID_CACHE = 10_002,
  INVALID_ADDRESS = 10_003,
  FETCH_FROM_CACHE_ERROR = 10_004,
  REQUEST_TIMEOUT = 10_005,
  THROTTLER_ERROR = 10_006,

  INTERNAL_ERROR = 11_000,
  INVALID_EXCHANGE_ID = 11_001,
  INVALID_EXCHANGE_INSTANCE_ID = 11_002,

  AUTHENTICATION_ERROR = 12_000,
  INVALID_CREDENTIALS = 12_001,
  AUTHENTICATION_FLOW_NOT_SUPPORTED = 12_002,
  OAUTH_ACCESS_REVOKED = 12_003,
  KYC_ERROR = 12_004,
  REFERRAL_REQUIRED = 12_005,
  AGENT_WALLET_NOT_FOUND = 12_006,

  WITHDRAWAL_ERROR = 13_000,
  READONLY_EXCHANGE_NOT_PROVIDED_ERROR = 13_001,
  TOKEN_SYMBOL_NOT_FOUND = 13_002,
  WITHDRAWAL_NOT_FOUND = 13_003,
  UNDER_WITHDRAW_MINIMUM = 13_004,
  OVER_WITHDRAW_MAXIMUM = 13_005,
  WHITELIST_CHECK_FAILED = 13_006,
  WITHDRAWAL_FORBIDDEN = 13_007,

  TRADE_ERROR = 14_000,
  MARKET_ERROR = 14_001,
  MARKET_NOT_FOUND = 14_002,
  ORDER_NOT_FOUND = 14_003,
  MARKET_NOT_LOADED = 14_004,
  MARKET_TYPE_NOT_SUPPORTED = 14_005,
  INVALID_OHLCV_FORMAT = 14_006,
  CANNOT_PROVIDE_OHLCV = 14_007,
  INVALID_STP_TYPE = 14_008,
  INVALID_ORDER = 14_009,
  ORDER_BELOW_MINIMUM = 14_016,
  ORDER_PRICE_INVALID = 14_017,
  DUPLICATE_ORDER_ID = 14_010,
  ORDER_IMMEDIATELY_FILLABLE = 14_011,
  ORDER_NOT_FILLABLE = 14_012,
  CONTRACT_UNAVAILABLE = 14_013,
  MAX_LEVERAGE_OUT_OF_BOUNDS = 14_014,
  MARKET_RESTRICTED = 14_015,
  LEVERAGE_NOT_MODIFIED = 14_018,
  MAX_POSITION_EXCEEDED = 14_019,
  REDUCE_ONLY_REJECTED = 14_020,

  PORTFOLIO_ERROR = 15_000,
  NOT_ENOUGH_BALANCE = 15_001,
  INSUFFICIENT_MARGIN = 15_002,

  DEPOSIT_ERROR = 16_000,
  GET_DEPOSIT_ADDRESS = 16_001,
  CREATE_DEPOSIT_ADDRESS = 16_002,
  DEPOSIT_NOT_FOUND = 16_003,
  DEPOSIT_ADDRESS_ALREADY_EXISTS = 16_004,

  NETWORK_ERROR = 17_000,
  NETWORK_NOT_FOUND = 17_001,
  NETWORK_NOT_FOUND_FOR_TOKEN_SYMBOL = 17_002,

  API_ERROR = 18_000,
  INVALID_API_REQUEST = 18_001,
  INTERNAL_API_EXCEPTION = 18_002,

  EXCHANGE_ERROR = 19_000,
  EXCHANGE_SERVER_DOWN = 19_001,
  EXCHANGE_UNDER_MAINTENANCE = 19_002,
  EXCHANGE_REQUEST_EXPIRED = 19_003,
  EXCHANGE_INVALID_PERMISSION = 19_004,
  EXCHANGE_TRANSFER_NOT_ALLOWED = 19_005,
  EXCHANGE_TRANSFER_ERROR = 19_006,
  EXCHANGE_DDOS_PROTECTION = 19_007,
  EXCHANGE_RATE_LIMIT_EXCEEDED = 19_008,
  EXCHANGE_INTERNAL_ERROR = 19_009,
  EXCHANGE_UNKNOWN_ASSET = 19_010,
  EXCHANGE_OPERATION_FAILED = 19_011,
  EXCHANGE_ACCOUNT_SUSPENDED = 19_012,
  EXCHANGE_NOT_ALLOWED_METHOD = 19_013,
  EXCHANGE_NOT_AVAILABLE = 19_014,
  EXCHANGE_INVALID_NONCE = 19_015,
  EXCHANGE_UNAVAILABLE_IN_COUNTRY = 19_016,
  EXCHANGE_WALLET_TYPE_NOT_SUPPORTED = 19_017,
  EXCHANGE_OPERATION_NOT_ALLOWED_FOR_SUB_ACCOUNT = 19_018,
  EXCHANGE_POSITION_SIDE_MISMATCH = 19_019,
  EXCHANGE_MISSING_TRADE_HISTORY_PERMISSION = 19_020,

  SUB_ACCOUNT_ERROR = 20_000,
  SUB_ACCOUNT_NOT_FOUND_ERROR = 20_001,
  SUB_ACCOUNT_NOT_ENABLED_ERROR = 20_002,

  EXCHANGE_CLOSED_BY_USER = 21_000,
  UNSUBSCRIBE_ERROR = 21_001,
}

/**
 * @description It aggregates all the error messages that can be thrown by the SDK.
 * You should separate each error type by a blank line.
 */
export const SdkErrorMessages = {
  DEFAULT_ERROR: "Default error",
  INVALID_PARAMS: (description: string) => `Invalid params: ${description}`,
  INVALID_CACHE: (key: string) => `Invalid cache state for key: ${key}`,
  INVALID_ADDRESS: "Invalid address",
  FETCH_FROM_CACHE_ERROR: (message: string) => `An error occured while fetching from the cache: ${message}`,
  REQUEST_TIMEOUT: (message: string) => `Request timeout: ${message}`,

  INTERNAL_ERROR: "Internal error",
  INVALID_EXCHANGE_INSTANCE_ID: (exchangeInstanceId: string) => `Invalid exchange instance id: ${exchangeInstanceId}`,
  INVALID_EXCHANGE_ID: (exchangeId: string) => `Invalid exchange id: ${exchangeId}`,

  AUTHENTICATION_ERROR: "Authentication error",
  INVALID_CREDENTIALS: "Invalid credentials provided",
  INVALID_API_KEY_IP_OR_PERMISSIONS:
    "Your API key was rejected: it is invalid, IP-restricted, or missing a required permission (e.g. Futures for perps trading). Check the key's permissions and IP whitelist, or reconnect with a new key.",
  AUTHENTICATION_FLOW_NOT_SUPPORTED: "Authentication flow not supported",
  OAUTH_ACCESS_REVOKED: "OAuth access revoked",
  KYC_ERROR: "KYC error",
  REFERRAL_REQUIRED:
    'Hotstuff requires onboarding with a referral code before connecting your account. Please sign up on Hotstuff using referral code "not-functional-code" and try again.',
  AGENT_WALLET_NOT_FOUND: "Agent wallet is not registered on the exchange. Please reconnect your wallet.",

  WITHDRAWAL_ERROR: "Withdrawal error",
  READONLY_EXCHANGE_NOT_PROVIDED_ERROR: "Readonly exchange account not provided",
  TOKEN_SYMBOL_NOT_FOUND: (tokenSymbol: string) => `Token symbol not found: ${tokenSymbol}`,
  WITHDRAWAL_NOT_FOUND: "Withdrawal not found",
  UNDER_WITHDRAW_MINIMUM: "Under withdrawal minimum",
  OVER_WITHDRAW_MAXIMUM: "Over withdrawal maximum",
  WHITELIST_CHECK_FAILED: "The receipt address has not been whitelisted",
  WITHDRAWAL_FORBIDDEN: "Withdrawal forbidden",

  TRADE_ERROR: "Trade error",
  MARKET_ERROR: "Market error",
  MARKET_NOT_FOUND: (market: string) => `Market not found: ${market}`,
  ORDER_NOT_FOUND: "Order not found",
  INVALID_ORDER: "Invalid order",
  ORDER_BELOW_MINIMUM: "Order size or notional below exchange minimum",
  ORDER_PRICE_INVALID: "Order price is invalid (tick size, distance from reference, etc.)",
  MARKET_NOT_LOADED: `Market not loaded`,
  MARKET_TYPE_NOT_SUPPORTED: (marketType: string) => `Market type not supported: ${marketType}`,
  ORDER_UNDER_MINIMUM:
    "Order quantity below minimum limit. Please use getMinAmounts() to get the minimum quantity for the order.",
  INVALID_OHLCV_FORMAT: (message: string) => `Invalid OHLCV format: ${message}`,
  CANNOT_PROVIDE_OHLCV: `Cannot provide OHLCV data`,
  INVALID_STP_TYPE: (stpType: string) => `Invalid STP type: ${stpType}`,
  DUPLICATE_ORDER_ID: "Duplicate order ID",
  ORDER_IMMEDIATELY_FILLABLE: "Order would be immediately filled (post-only order cannot be placed)",
  ORDER_NOT_FILLABLE: "Order cannot be filled (IOC order would not execute)",
  CONTRACT_UNAVAILABLE: "Contract is unavailable for trading",
  MAX_LEVERAGE_OUT_OF_BOUNDS: "Selected leverage exceeds the allowed limit.",
  MARKET_RESTRICTED: "Trading on this market is currently restricted. Your account is not permitted to place trades.",
  LEVERAGE_NOT_MODIFIED: "Leverage is already set to the requested value",
  MAX_POSITION_EXCEEDED: "Order would exceed your maximum position limit for this contract. Reduce the order size.",
  REDUCE_ONLY_REJECTED:
    "Reduce-only order rejected — the position may already be closed or smaller than the requested amount. Refresh and try again.",

  PORTFOLIO_ERROR: "Portfolio error",
  NOT_ENOUGH_BALANCE: "Not enough balance",
  INSUFFICIENT_MARGIN: "Insufficient margin to place order",

  DEPOSIT_ERROR: "Deposit error",
  DEPOSIT_NOT_FOUND: "Deposit not found",
  DEPOSIT_ADDRESS_ALREADY_EXISTS: `The deposit address already exists`,
  GET_DEPOSIT_ADDRESS: (tokenSymbol: string, network?: string) =>
    `An error occured while fetching the deposit address for token symbol ${tokenSymbol}${
      network ? " on network " + network : ""
    }`,
  CREATE_DEPOSIT_ADDRESS: (tokenSymbol: string, network?: string) =>
    `An error occured while creating the deposit address for token symbol ${tokenSymbol}${
      network ? " on network " + network : ""
    }`,

  NETWORK_ERROR: "Network error",
  NETWORK_NOT_FOUND: (network: string) => `Network not found: ${network}`,
  NETWORK_NOT_FOUND_FOR_TOKEN_SYMBOL: (tokenSymbol: string) => `Network not found for token symbol: ${tokenSymbol}`,

  API_ERROR: "API error",
  INVALID_API_REQUEST: (errorMessage: string) => `Invalid request: ${errorMessage}`,

  EXCHANGE_ERROR: "Exchange error",
  EXCHANGE_SERVER_DOWN: "Exchange server down",
  EXCHANGE_UNDER_MAINTENANCE: "Exchange under maintenance",
  EXCHANGE_REQUEST_EXPIRED: "Exchange request expired",
  EXCHANGE_INVALID_PERMISSION: "Missing API permissions, please reimport your API key with the valid permissions",
  EXCHANGE_TRANSFER_NOT_ALLOWED: `Transfer not allowed`,
  EXCHANGE_TRANSFER_ERROR: `Transfer error`,
  EXCHANGE_DDOS_PROTECTION: "DDoS protection error",
  EXCHANGE_RATE_LIMIT_EXCEEDED: "Rate limit exceeded",
  EXCHANGE_INTERNAL_ERROR: "Internal exchange error",
  EXCHANGE_UNKNOWN_ASSET: (asset: string) => `Unknown asset ${asset}`,
  EXCHANGE_OPERATION_FAILED: "Operation failed",
  EXCHANGE_ACCOUNT_SUSPENDED: "Account suspended",
  EXCHANGE_NOT_ALLOWED_METHOD: (methodName: string) => `Method not allowed: ${methodName}`,
  EXCHANGE_NOT_AVAILABLE: "Exchange not available",
  EXCHANGE_INVALID_NONCE: "Invalid nonce",
  EXCHANGE_UNAVAILABLE_IN_COUNTRY: "Exchange unavailable in the country",
  EXCHANGE_WALLET_TYPE_NOT_SUPPORTED: (walletType: string) =>
    `Wallet type ${walletType} not supported or not available for transfers`,
  EXCHANGE_OPERATION_NOT_ALLOWED_FOR_SUB_ACCOUNT: "This operation is not permitted for sub-accounts",
  EXCHANGE_POSITION_SIDE_MISMATCH: "Order position side does not match the account position mode",
  EXCHANGE_MISSING_TRADE_HISTORY_PERMISSION:
    "Missing trade history permission, please reimport your API key with the trade history permission enabled",

  SUB_ACCOUNT_ERROR: "Sub account error",
  SUB_ACCOUNT_NOT_FOUND_ERROR: (uid: string) => `Sub account not found for uid: ${uid}`,
  SUB_ACCOUNT_NOT_ENABLED_ERROR: "Sub account not enabled",

  EXCHANGE_CLOSED_BY_USER: "Exchange closed by user",
};

/**
 * @description Stable error name constants.
 * Used by error classes (this.name = ...) and by consumers for reliable
 * name-based matching that survives minification.
 */
export const SdkErrorNames = {
  // Trade errors
  INVALID_ORDER: "InvalidOrderError",
  ORDER_NOT_FOUND: "OrderNotFoundError",
  ORDER_BELOW_MINIMUM: "OrderBelowMinimumError",
  ORDER_PRICE_INVALID: "OrderPriceInvalidError",
  ORDER_IMMEDIATELY_FILLABLE: "OrderImmediatelyFillableError",
  TRADE_ERROR: "TradeError",
  MAX_POSITION_EXCEEDED: "MaxPositionExceededError",
  REDUCE_ONLY_REJECTED: "ReduceOnlyRejectedError",

  // Portfolio / balance errors
  INSUFFICIENT_BALANCE: "InsufficientBalanceError",
  INSUFFICIENT_MARGIN: "InsufficientMarginError",

  // Exchange errors (transient)
  EXCHANGE_REQUEST_EXPIRED: "ExchangeRequestExpiredError",
  EXCHANGE_RATE_LIMIT_EXCEEDED: "ExchangeRateLimitExceededError",
  EXCHANGE_SERVER_DOWN: "ExchangeServerDownError",
  EXCHANGE_NOT_AVAILABLE: "ExchangeNotAvailableError",
  EXCHANGE_DDOS_PROTECTION: "ExchangeDDosProtectionError",
  EXCHANGE_OPERATION_FAILED: "ExchangeOperationFailedError",
  EXCHANGE_INVALID_NONCE: "ExchangeInvalidNonceError",
  EXCHANGE_POSITION_SIDE_MISMATCH: "ExchangePositionSideMismatchError",
  REQUEST_TIMEOUT: "RequestTimeout",
  NETWORK_ERROR: "NetworkError",
} as const;

/**
 * @description It aggregates all the documentation of the SDK errors.
 * It should be used in the sdk documentation website.
 * The first element in the array is the error class that is thrown and the second element is the error description.
 *
 * [Error Class, Error Description, ParentErrorClass?]
 */
export const SdkErrorDocumentation = {
  "Cede SDK Errors": {
    [SdkErrorCodes.DEFAULT_ERROR]: ["DefaultError", "Default SDK error"],
    [SdkErrorCodes.INVALID_PARAMS]: [
      "InvalidParamsError",
      "Invalid parameters provided to a SDK method",
      "DefaultError",
    ],
    [SdkErrorCodes.INVALID_CACHE]: ["InvalidCacheError", "Thrown when the cache can't be persisted", "DefaultError"],
    [SdkErrorCodes.INVALID_ADDRESS]: ["InvalidAddressError", "The provided address is invalid", "DefaultError"],
    [SdkErrorCodes.FETCH_FROM_CACHE_ERROR]: [
      "FetchFromCacheError",
      "An error occured while fetching from the cache",
      "DefaultError",
    ],
    [SdkErrorCodes.REQUEST_TIMEOUT]: ["RequestTimeout", "Request timeout", "DefaultError"],
  },
  "Internal Errors": {
    [SdkErrorCodes.INTERNAL_ERROR]: ["InternalError", "Default error for internal SDK errors"],
    [SdkErrorCodes.INVALID_EXCHANGE_INSTANCE_ID]: [
      "InvalidExchangeInstanceIdError",
      "An invalid exchange instance id is provided",
      "InternalError",
    ],
    [SdkErrorCodes.INVALID_EXCHANGE_ID]: [
      "InvalidExchangeIdError",
      "An invalid exchange id is provided",
      "InternalError",
    ],
  },
  "Authentication Errors": {
    [SdkErrorCodes.AUTHENTICATION_ERROR]: ["AuthenticationError", "Default error for authentication errors"],
    [SdkErrorCodes.INVALID_CREDENTIALS]: [
      "InvalidCredentialsError",
      "Invalid credentials provided",
      "AuthenticationError",
    ],
    [SdkErrorCodes.AUTHENTICATION_FLOW_NOT_SUPPORTED]: [
      "AuthenticationFlowNotSupportedError",
      "Authentication flow not supported",
      "AuthenticationError",
    ],
    [SdkErrorCodes.OAUTH_ACCESS_REVOKED]: [
      "OAuthAccessRevokedError",
      "Can't perform the operation since the OAuth access are revoked",
      "AuthenticationError",
    ],
    [SdkErrorCodes.REFERRAL_REQUIRED]: [
      "ReferralRequiredError",
      "Hotstuff rejected the action because the master wallet has not completed onboarding with a referral code",
      "AuthenticationError",
    ],
    [SdkErrorCodes.AGENT_WALLET_NOT_FOUND]: [
      "AgentWalletNotFoundError",
      "The agent/API wallet signing the request is not registered as an approved signer for the master account on the exchange",
      "AuthenticationError",
    ],
  },
  "Withdrawal Errors": {
    [SdkErrorCodes.WITHDRAWAL_ERROR]: ["WithdrawalError", "Default error for withdrawal errors"],
    [SdkErrorCodes.READONLY_EXCHANGE_NOT_PROVIDED_ERROR]: [
      "ReadonlyExchangeNotProvidedError",
      "Readonly account not provided. It needs to be provided to perform some operations",
      "WithdrawalError",
    ],
    [SdkErrorCodes.TOKEN_SYMBOL_NOT_FOUND]: [
      "TokenSymbolNotFoundError",
      "The withdrawal can't be executed since the token symbol isn't found",
      "WithdrawalError",
    ],
    [SdkErrorCodes.WITHDRAWAL_NOT_FOUND]: ["WithdrawalNotFoundError", "The withdrawal isn't found", "WithdrawalError"],
    [SdkErrorCodes.UNDER_WITHDRAW_MINIMUM]: [
      "UnderWithdrawMinimumError",
      "The withdrawal amount is under the minimum amount allowed by the exchange",
      "WithdrawalError",
    ],
    [SdkErrorCodes.OVER_WITHDRAW_MAXIMUM]: [
      "OverWithdrawMaximumError",
      "The withdrawal amount is over the maximum amount allowed by the exchange",
      "WithdrawalError",
    ],
    [SdkErrorCodes.WHITELIST_CHECK_FAILED]: [
      "WhitelistCheckFailedError",
      "The provided address isn't whitelisted for the withdrawal",
      "WithdrawalError",
    ],
  },

  "Trade Errors": {
    [SdkErrorCodes.TRADE_ERROR]: ["TradeError", "Default error for trade errors"],
    [SdkErrorCodes.REDUCE_ONLY_REJECTED]: [
      "ReduceOnlyRejectedError",
      "The exchange rejected a reduce-only order because it would not reduce an existing position",
      "TradeError",
    ],
    [SdkErrorCodes.MARKET_NOT_FOUND]: ["MarketNotFoundError", "The given pair symbol was not found", "TradeError"],
    [SdkErrorCodes.ORDER_NOT_FOUND]: ["OrderNotFoundError", "The order isn't found", "TradeError"],
    [SdkErrorCodes.CANNOT_PROVIDE_OHLCV]: ["CannotProvideOHLCVError", "Cannot provide OHLCV data", "TradeError"],
    [SdkErrorCodes.INVALID_OHLCV_FORMAT]: ["InvalidOHLCVFormatError", "The OHLCV format is invalid", "TradeError"],
    [SdkErrorCodes.MARKET_ERROR]: ["MarketError", "Default error for market errors", "TradeError"],
    [SdkErrorCodes.MARKET_TYPE_NOT_SUPPORTED]: [
      "MarketTypeNotSupportedError",
      "The market type isn't supported",
      "MarketError",
    ],
    [SdkErrorCodes.MARKET_NOT_LOADED]: ["MarketNotLoadedError", "The market isn't loaded", "MarketError"],
  },

  "Portfolio Errors": {
    [SdkErrorCodes.PORTFOLIO_ERROR]: ["PortfolioError", "Default error for portfolio errors"],
    [SdkErrorCodes.NOT_ENOUGH_BALANCE]: [
      "InsufficientBalanceError",
      "The user doesn't have enough balance to perform the operation",
      "PortfolioError",
    ],
  },

  "Deposit Errors": {
    [SdkErrorCodes.DEPOSIT_ERROR]: ["DepositError", "Default error for deposit errors"],
    [SdkErrorCodes.GET_DEPOSIT_ADDRESS]: ["GetDepositAddressError", "Can't get the deposit address", "DepositError"],
    [SdkErrorCodes.CREATE_DEPOSIT_ADDRESS]: [
      "CreateDepositAddressError",
      "Can't create the deposit address",
      "DepositError",
    ],
  },

  "Network Errors": {
    [SdkErrorCodes.NETWORK_ERROR]: ["NetworkError", "Default error for network errors"],
    [SdkErrorCodes.NETWORK_NOT_FOUND]: ["NetworkNotFoundError", "The network isn't found", "NetworkError"],
  },

  "API Errors": {
    [SdkErrorCodes.API_ERROR]: ["APIError", "Default error for API errors"],
    [SdkErrorCodes.INVALID_API_REQUEST]: ["InvalidApiRequestError", "The API request is invalid", "APIError"],
  },

  "Exchange Errors": {
    [SdkErrorCodes.EXCHANGE_ERROR]: ["ExchangeError", "Default error for exchange errors"],
    [SdkErrorCodes.EXCHANGE_SERVER_DOWN]: ["ExchangeServerError", "The exchange server is down", "ExchangeError"],
    [SdkErrorCodes.EXCHANGE_UNDER_MAINTENANCE]: [
      "ExchangeUnderMaintenanceError",
      "The exchange API is under maintenance",
      "ExchangeError",
    ],
    [SdkErrorCodes.EXCHANGE_REQUEST_EXPIRED]: [
      "ExchangeRequestExpiredError",
      "The exchange request is expired",
      "ExchangeError",
    ],
    [SdkErrorCodes.EXCHANGE_INVALID_PERMISSION]: [
      "ExchangeInvalidPermissionsError",
      "The permissions are invalid to perform the operation",
      "ExchangeError",
    ],
    [SdkErrorCodes.EXCHANGE_TRANSFER_NOT_ALLOWED]: [
      "ExchangeTransferNotAllowedError",
      "Transfer is not allowed",
      "ExchangeError",
    ],
    [SdkErrorCodes.EXCHANGE_TRANSFER_ERROR]: ["ExchangeTransferError", "Transfer error", "ExchangeError"],
    [SdkErrorCodes.EXCHANGE_DDOS_PROTECTION]: ["ExchangeDDosProtectionError", "DDoS protection error", "ExchangeError"],
    [SdkErrorCodes.EXCHANGE_RATE_LIMIT_EXCEEDED]: [
      "ExchangeRateLimitExceededError",
      "Rate limit exceeded",
      "ExchangeError",
    ],
    [SdkErrorCodes.EXCHANGE_OPERATION_FAILED]: ["ExchangeOperationFailedError", "Operation failed", "ExchangeError"],
    [SdkErrorCodes.EXCHANGE_POSITION_SIDE_MISMATCH]: [
      "ExchangePositionSideMismatchError",
      "Order position side does not match the account position mode (one-way vs hedge)",
      "ExchangeError",
    ],
    [SdkErrorCodes.EXCHANGE_MISSING_TRADE_HISTORY_PERMISSION]: [
      "ExchangeMissingTradeHistoryPermissionError",
      "The API key can't read the account's trade history",
      "ExchangeError",
    ],
    [SdkErrorCodes.EXCHANGE_UNKNOWN_ASSET]: ["ExchangeUnknownAssetError", "Unknown asset", "ExchangeError"],
    [SdkErrorCodes.EXCHANGE_ACCOUNT_SUSPENDED]: ["ExchangeAccountSuspendedError", "Account suspended", "ExchangeError"],
    [SdkErrorCodes.EXCHANGE_NOT_ALLOWED_METHOD]: [
      "ExchangeNotAllowedMethodError",
      "Method isn't allowed to be called for an exchange",
      "ExchangeError",
    ],
    [SdkErrorCodes.EXCHANGE_NOT_AVAILABLE]: ["ExchangeNotAvailableError", "Exchange is not available", "ExchangeError"],
    [SdkErrorCodes.EXCHANGE_INVALID_NONCE]: ["ExchangeInvalidNonceError", "The nonce is invalid", "ExchangeError"],
    [SdkErrorCodes.EXCHANGE_UNAVAILABLE_IN_COUNTRY]: [
      "ExchangeUnavailableInCountryError",
      "Exchange is unavailable in the country",
      "ExchangeError",
    ],
    [SdkErrorCodes.EXCHANGE_WALLET_TYPE_NOT_SUPPORTED]: [
      "ExchangeWalletTypeNotSupportedError",
      "Wallet type not supported or not available for transfers",
      "ExchangeError",
    ],
    [SdkErrorCodes.EXCHANGE_CLOSED_BY_USER]: ["ExchangeClosedByUser", "Exchange closed by user", "ExchangeError"],
  },
};
